import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Sparkles } from "lucide-react";
import { setTenantId } from "@/lib/tenant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * First-run setup wizard for a brand-new PayPunch tenant.
 * Rendered when GET /api/tenant/current returns exists=false.
 * Once submitted the tenant is created empty and the page reloads
 * into the normal PayPunch login screen — scoped to that tenant.
 */
export default function TenantSetupWizard({ tenantId, onComplete }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    address: "",
    phone: "",
    close_time: "20:00",
    open_time: "09:00",
    master_pins: "",
    primary_color: "#A61B1B",
    contact_email: "",
    state: "IL",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Business name is required");
    if (!form.master_pins.trim()) return toast.error("At least one master PIN is required");
    setSaving(true);
    try {
      await axios.post(
        `${API}/tenant/setup`,
        {
          tenant_id: tenantId,
          name: form.name.trim(),
          tagline: form.tagline.trim(),
          address: form.address.trim(),
          phone: form.phone.trim(),
          open_time: form.open_time,
          close_time: form.close_time,
          master_pins: form.master_pins.split(",").map((s) => s.trim()).filter(Boolean),
          primary_color: form.primary_color,
          contact_email: form.contact_email.trim(),
          state: form.state.trim().toUpperCase(),
        },
        { headers: { "X-Tenant-Id": tenantId } }
      );
      setTenantId(tenantId);
      toast.success(`Welcome to PayPunch, ${form.name}!`);
      if (onComplete) onComplete();
      else setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Setup failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F1013] via-[#1A1A1A] to-[#2A0E0E] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#A61B1B] to-[#6B0F0F] text-white px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur">
              <Sparkles className="w-7 h-7" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-200 font-bold">
                Powered by AV Tech
              </div>
              <h1 className="text-2xl font-extrabold leading-tight">Welcome to PayPunch</h1>
              <p className="text-xs text-white/85 mt-0.5">
                Setting up tenant: <span className="font-mono font-bold">{tenantId}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-8 space-y-4">
          <p className="text-sm text-stone-600 flex items-start gap-2">
            <Building2 className="w-4 h-4 mt-0.5 shrink-0 text-[#A61B1B]" />
            <span>
              This is a brand-new PayPunch instance. Tell us about your business — you can change
              anything later in Settings.
            </span>
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Business name *</Label>
              <Input
                data-testid="setup-name"
                value={form.name}
                onChange={set("name")}
                placeholder="AV Stones"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Tagline</Label>
              <Input
                data-testid="setup-tagline"
                value={form.tagline}
                onChange={set("tagline")}
                placeholder="Powered by AV Tech"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Address</Label>
              <Input
                data-testid="setup-address"
                value={form.address}
                onChange={set("address")}
                placeholder="123 Main St, Chicago, IL"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                data-testid="setup-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="847-555-0123"
              />
            </div>
            <div>
              <Label>State</Label>
              <Input
                data-testid="setup-state"
                value={form.state}
                onChange={set("state")}
                maxLength={2}
              />
            </div>
            <div>
              <Label>Open time</Label>
              <Input
                data-testid="setup-open-time"
                type="time"
                value={form.open_time}
                onChange={set("open_time")}
              />
            </div>
            <div>
              <Label>Close time</Label>
              <Input
                data-testid="setup-close-time"
                type="time"
                value={form.close_time}
                onChange={set("close_time")}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Master PINs (comma-separated) *</Label>
              <Input
                data-testid="setup-pins"
                value={form.master_pins}
                onChange={set("master_pins")}
                placeholder="12345, 67890"
              />
              <p className="text-[11px] text-stone-500 mt-1">
                These PINs unlock the master dashboard, kiosk exit, and payroll. Keep them secret.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>Contact email (for reports)</Label>
              <Input
                data-testid="setup-email"
                type="email"
                value={form.contact_email}
                onChange={set("contact_email")}
                placeholder="you@yourbusiness.com"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[11px] text-stone-500">
              No data will be shared with other PayPunch tenants.
            </p>
            <Button
              data-testid="setup-submit"
              onClick={submit}
              disabled={saving}
              className="bg-[#A61B1B] hover:bg-[#841515] text-white px-8 h-11 font-bold"
            >
              {saving ? "Creating…" : "Create Tenant →"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
